import { Text } from "@chakra-ui/react";
import { createContext, type ReactNode, useContext } from "react";
import { useAnnouncement } from "../hooks/announcements.hooks";
import { cn } from "../lib/utils";
import {
	AnnouncementStyle,
	AnnouncementType,
	type Announcement as TAnnouncement,
} from "../types/announcement.types";

const AnnouncementContext = createContext<TAnnouncement>({
	type: AnnouncementType.GLOBAL,
	published: false,
	title: "",
	content: "",
	style: AnnouncementStyle.INFO,
});

const AnnouncementTitle = () => {
	const { title, type } = useContext(AnnouncementContext);

	return (
		<Text
			fontSize={type === AnnouncementType.GLOBAL ? "21px" : "14px"}
			fontWeight={600}
		>
			{title}
		</Text>
	);
};

const AnnouncementContent = () => {
	const { content } = useContext(AnnouncementContext);

	return (
		<Text fontSize="14px" fontWeight={400} whiteSpace="pre-line">
			{content}
		</Text>
	);
};

const AnnouncementContainer = ({
	children,
	infoStyle = "border-brand-200! bg-brand-50",
	errorStyle = "border-error-200! bg-error-50",
}: {
	children: ReactNode;
	infoStyle?: string;
	errorStyle?: string;
}) => {
	const { style } = useContext(AnnouncementContext);

	return (
		<div
			className={cn("border! rounded-md p-2", {
				[infoStyle]: style === AnnouncementStyle.INFO,
				[errorStyle]: style === AnnouncementStyle.ERROR,
			})}
		>
			{children}
		</div>
	);
};

type AnnouncementProps = {
	type: AnnouncementType;
	infoStyle?: string;
	errorStyle?: string;
};

const Announcement = (props: AnnouncementProps) => {
	const { data: announcement } = useAnnouncement(props.type);

	if (!announcement || announcement.published === false) return;
	return (
		<AnnouncementContext.Provider value={announcement}>
			<AnnouncementContainer {...props}>
				<AnnouncementTitle />
				<AnnouncementContent />
			</AnnouncementContainer>
		</AnnouncementContext.Provider>
	);
};

export default Announcement;
